// How-to-play content: the welcome card, the Help sheet and one-time tips. Short, calm, plain words,
// in English and Pidgin. Touch devices get touch controls, keyboards get keys.
import { lang } from "./i18n";
import { helpOpen, showToast } from "./bridge";

export const touch = () => typeof matchMedia === "function" && matchMedia("(hover: none)").matches;

type Txt = { en: string; pcm: string };
export const tx = (t: Txt) => (lang.value === "pcm" ? t.pcm : t.en);

export interface Step {
  icon: string;
  title: Txt;
  body: () => Txt;
}

/** The four things a new player needs. Shown once after arrival, and at the top of Help. */
export const WELCOME: Step[] = [
  {
    icon: "🚶",
    title: { en: "Move around", pcm: "Waka around" },
    body: () =>
      touch()
        ? { en: "Tap the ground to walk there. Drag to look around. Pinch to zoom.", pcm: "Tap ground make you waka reach there. Drag to look around. Pinch to zoom." }
        : { en: "WASD or arrow keys to walk, Shift to run. Or click the ground. Drag to look around.", pcm: "Use WASD or arrow keys waka, hold Shift to run. Or click ground. Drag to look around." },
  },
  {
    icon: "📱",
    title: { en: "Your phone does everything", pcm: "Your phone na everything" },
    body: () => ({
      en: `Trade, post, text, buy cars and houses, all from your phone. ${touch() ? "Tap 📱" : "Press P or click 📱"} to open it.`,
      pcm: `Trade, post, text people, buy motor and house, everything dey your phone. ${touch() ? "Tap 📱" : "Press P or click 📱"} to open am.`,
    }),
  },
  {
    icon: "💰",
    title: { en: "Make (play) money", pcm: "Hustle (play) money" },
    body: () => ({
      en: "Farm airdrops, buy memecoins early, give people rides. It's all play money. No real money, no wallet, ever.",
      pcm: "Farm airdrop, buy memecoin early, carry people for your motor. Na play money, no real money, no wallet at all.",
    }),
  },
  {
    icon: "🐋",
    title: { en: "Plankton to Whale", pcm: "From Plankton reach Whale" },
    body: () => ({
      en: "Grow your money and followers to rank up. Not sure what's next? Follow the quest card. It can take you there.",
      pcm: "Grow your money and followers make you rank up. You no sure wetin to do? Follow the quest card, e fit carry you go there.",
    }),
  },
];

export interface HelpSection {
  icon: string;
  title: Txt;
  lines: () => Txt[];
}

export const HELP: HelpSection[] = [
  {
    icon: "🎮",
    title: { en: "Controls", pcm: "How to control" },
    lines: () =>
      touch()
        ? [
            { en: "Tap the ground: walk there. Tap a real player: wave, ride, invite.", pcm: "Tap ground: waka go there. Tap real player: wave, ride, invite." },
            { en: "Drag: look around. Pinch: zoom. 🏃 button: run.", pcm: "Drag: look around. Pinch: zoom. 🏃 button: run." },
            { en: "Green button at the bottom: go inside, open apps, sleep.", pcm: "Green button for down: enter inside, open app, sleep." },
            { en: "In a car: the pedals and arrows on screen.", pcm: "Inside motor: use the pedal and arrow wey dey screen." },
          ]
        : [
            { en: "WASD / arrows: walk (drive in a car). Shift: run.", pcm: "WASD / arrows: waka (or drive motor). Shift: run." },
            { en: "E: use the green button (go inside, open apps, sleep).", pcm: "E: press the green button (enter, open app, sleep)." },
            { en: "P: phone. M: map. Q / E or drag: turn the camera. Scroll: zoom.", pcm: "P: phone. M: map. Q / E or drag: turn camera. Scroll: zoom." },
            { en: "Space: pause. 1 / 2 / 3: game speed.", pcm: "Space: pause. 1 / 2 / 3: game speed." },
            { en: "In a car: H highway, R radio, T next station.", pcm: "Inside motor: H highway, R radio, T next station." },
          ],
  },
  {
    icon: "🗓️",
    title: { en: "A good first day", pcm: "Better first day" },
    lines: () => [
      { en: "1. Walk to the Farm and start a testnet task (free points).", pcm: "1. Waka go Farm, start testnet task (free points)." },
      { en: "2. Open Chirp and say gm. Follow people who post alpha.", pcm: "2. Open Chirp, talk gm. Follow people wey dey post alpha." },
      { en: "3. Buy a small bag of a trending coin on Pumpr.", pcm: "3. Buy small bag of coin wey dey trend for Pumpr." },
      { en: "4. Go Home and sleep to end the day. Prices keep moving overnight.", pcm: "4. Go house sleep to end the day. Price go still dey move for night." },
    ],
  },
  {
    icon: "💸",
    title: { en: "Money", pcm: "Money" },
    lines: () => [
      { en: "W$ (WUSD) is your cash. Everything is priced in it.", pcm: "W$ (WUSD) na your cash. Everything get price for am." },
      { en: "⛽ DGN is gas: small fees for on-chain stuff. Check in daily in Wallet for free DGN.", pcm: "⛽ DGN na gas: small fee for on-chain things. Check in every day for Wallet, collect free DGN." },
      { en: "Most coins go up for a while, some run 100x, a few rug to zero. Don't put everything in one coin.", pcm: "Many coin go up small, some run 100x, some go rug reach zero. No put all your money for one coin." },
    ],
  },
  {
    icon: "🛡️",
    title: { en: "Stay safe", pcm: "Shine your eye" },
    lines: () => [
      { en: "Never click links in DMs. Never share a seed phrase. Report scammers.", pcm: "No click link for DM. No ever share seed phrase. Report scammer." },
      { en: "If a deal sounds too good, it's a scam. The game pauses so you can think.", pcm: "If the deal too sweet, na scam. Game go pause make you think am well." },
      { en: "Heat goes up when you take risks. Coffee, touching grass and laying low cool it down.", pcm: "Heat dey rise when you take risk. Coffee, touch grass, and lay low go cool am down." },
    ],
  },
  {
    icon: "📖",
    title: { en: "Words you'll hear", pcm: "Words wey you go hear" },
    lines: () => [
      { en: "gm: good morning, the crypto hello.", pcm: "gm: good morning, na how crypto people dey greet." },
      { en: "Ape: buy fast without thinking. Bags: coins you hold.", pcm: "Ape: buy quick without thinking. Bags: coin wey you hold." },
      { en: "Rug: the creator runs with the money and the price goes to zero.", pcm: "Rug: the person wey create the coin run with the money, price go zero." },
      { en: "Moon / 100x: the price goes way up. Airdrop: free coins for early users.", pcm: "Moon / 100x: price go up well well. Airdrop: free coin for early people." },
      { en: "KOL: a big account people follow. DYOR: do your own research.", pcm: "KOL: big account wey people dey follow. DYOR: do your own research." },
    ],
  },
];

// ---------------------------------------------------------------- one-time tips
const TIP_KEY = "degen-world:tips";
function seen(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(TIP_KEY) ?? "[]"));
  } catch {
    return new Set();
  }
}

export const TIPS: Record<string, () => Txt> = {
  phone: () => ({
    en: `💡 Your phone: tap an app to open it. ${touch() ? "Swipe down" : "Press P or Esc"} to put it away.`,
    pcm: `💡 Your phone: tap app to open am. ${touch() ? "Swipe down" : "Press P or Esc"} to keep am.`,
  }),
  zone: () => ({
    en: `💡 ${touch() ? "Tap the green button" : "Press E or click the green button"} to go in or use this place.`,
    pcm: `💡 ${touch() ? "Tap the green button" : "Press E or click the green button"} to enter or use this place.`,
  }),
  drive: () => ({
    en: touch() ? "💡 Use the pedals to drive. Tap 🛣️ Road trip for the endless highway, 📻 for the radio." : "💡 W / S to drive, A / D to steer. H for the highway, R for the radio.",
    pcm: touch() ? "💡 Use the pedal drive. Tap 🛣️ Road trip for the long highway, 📻 for radio." : "💡 W / S to drive, A / D to turn. H for highway, R for radio.",
  }),
  person: () => ({
    en: "💡 Everyone you see walking around is a real player. Tap one to wave, offer a ride or invite them over.",
    pcm: "💡 Everybody wey you see dey waka na real player. Tap person to wave, offer ride or invite am come your house.",
  }),
};

/** Show a tip the first time only (per device). */
export function tipOnce(id: keyof typeof TIPS) {
  if (helpOpen.value) return; // not while the welcome card / Help is up
  const s = seen();
  if (s.has(id)) return;
  s.add(id);
  try {
    localStorage.setItem(TIP_KEY, JSON.stringify([...s]));
  } catch {
    /* storage blocked: the tip may show again next time, that's fine */
  }
  showToast(tx(TIPS[id]()));
}

const WELCOME_KEY = "degen-world:welcomed";
export function welcomed(): boolean {
  try {
    return localStorage.getItem(WELCOME_KEY) === "1";
  } catch {
    return false;
  }
}
export function markWelcomed() {
  try {
    localStorage.setItem(WELCOME_KEY, "1");
  } catch {
    /* fine */
  }
}
